import React from "react";
/* ============================================================
   Planned vs cost floors — one year, month by month (Budget → Plan).
   Hand-rolled SVG like the rest of the app (no chart library):
     bars   planned revenue (outline) beside booked revenue (completed,
            solid, with still-scheduled work stacked on top, lighter)
     lines  break-even (dashed), required for target net (solid),
            crew capacity (dotted; a step per month — months have
            different numbers of workdays)
   Drawn at the container's real pixel width so axis text stays legible
   on a phone instead of being scaled down with a viewBox. Hover (or tap)
   a month for the numbers behind it.
   ============================================================ */
const PC_money0 = (n: number) => window.VFP.money0(n);
const PC_short = (n: number) => {
  const a = Math.abs(n);
  if (a >= 999500) return "$" + (n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "") + "M";
  if (a >= 1000) return "$" + Math.round(n / 1000) + "k";
  return "$" + Math.round(n);
};
const PC_COL = { plan: "var(--teal)", booked: "var(--teal)", be: "var(--amber)", req: "var(--violet)", cap: "var(--fg-2)" };

/* width of an element, following resizes; `fallback` until measured */
function useWidth(ref: any, fallback: number) {
  const [w, setW] = React.useState<number>(fallback);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => { const x = el.getBoundingClientRect().width; if (x > 0) setW(Math.round(x)); };
    read();
    if (typeof ResizeObserver === "undefined") { window.addEventListener("resize", read); return () => window.removeEventListener("resize", read); }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function niceStep(max: number) {
  const raw = max / 4;
  const p = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const f = raw / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

function PlanChart({ months, breakeven, required, nowMonth, year }: any) {
  const box = React.useRef<any>(null);
  const W = useWidth(box, 640);
  const [active, setActive] = React.useState<number | null>(null);
  const narrow = W < 520;
  const H = narrow ? 210 : 240, L = narrow ? 38 : 48, R = 6, T = 10, B = 22;
  const pw = W - L - R, ph = H - T - B;

  const hasCap = months.some((m: any) => m.cap != null && m.cap > 0);
  const core = Math.max(1, ...months.map((m: any) => Math.max(m.plan, m.actual + m.sched)), breakeven || 0, required || 0);
  const capMax = hasCap ? Math.max(...months.map((m: any) => m.cap || 0)) : 0;
  // capacity far above everything else would squash the bars flat — past 1.6×
  // the rest it rides the top edge and the legend says so
  const capClipped = hasCap && capMax > core * 1.6;
  const top0 = Math.max(core, capClipped ? 0 : capMax) * 1.1;
  const step = niceStep(top0);
  const top = Math.ceil(top0 / step) * step;
  const y = (v: number) => T + ph - (Math.min(v, top) / top) * ph;
  const cw = pw / 12;
  const bw = Math.max(3, Math.min(16, cw * 0.32));
  const cx = (i: number) => L + cw * i + cw / 2;
  const ticks: number[] = [];
  for (let v = 0; v <= top + 0.5; v += step) ticks.push(v);

  const capPath = hasCap ? months.map((m: any, i: number) => {
    const yy = capClipped && (m.cap || 0) > top ? T + 1 : y(m.cap || 0);
    return `${i ? "L" : "M"}${(L + cw * i + 1).toFixed(1)},${yy.toFixed(1)} L${(L + cw * (i + 1) - 1).toFixed(1)},${yy.toFixed(1)}`;
  }).join(" ") : "";
  const a = active != null ? months[active] : null;
  // beside the hovered month, not on top of it: right of it in the first half, left in the second
  const TIP = 232;
  const tipLeft = active != null
    ? Math.max(4, Math.min(W - TIP - 4, cx(active) < W / 2 ? cx(active) + cw / 2 + 6 : cx(active) - cw / 2 - 6 - TIP))
    : 0;

  return (
    <div>
      <div ref={box} style={{ position: "relative", width: "100%" }} onMouseLeave={() => setActive(null)}>
        <svg width={W} height={H} role="img" aria-label={`Planned revenue vs cost floors by month, ${year}`} style={{ display: "block" }}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth="1" />
              <text x={L - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10.5" fill="var(--fg-3)" className="tnum">{PC_short(v)}</text>
            </g>
          ))}
          {months.map((m: any, i: number) => {
            const x0 = cx(i);
            const on = active === i;
            return (
              <g key={i}>
                {on && <rect x={L + cw * i} y={T} width={cw} height={ph} fill="var(--surface-2)" />}
                {/* planned: outlined */}
                {m.plan > 0 && <rect x={x0 - bw - 1} y={y(m.plan)} width={bw} height={Math.max(1, y(0) - y(m.plan))} rx="2"
                  fill="var(--teal-tint)" stroke={PC_COL.plan} strokeOpacity="0.55" strokeWidth="1" />}
                {/* booked: completed solid, scheduled stacked lighter */}
                {m.actual > 0 && <rect x={x0 + 1} y={y(m.actual)} width={bw} height={Math.max(1, y(0) - y(m.actual))} rx="2" fill={PC_COL.booked} />}
                {m.sched > 0 && <rect x={x0 + 1} y={y(m.actual + m.sched)} width={bw} height={Math.max(1, y(m.actual) - y(m.actual + m.sched) - (m.actual > 0 ? 2 : 0))} rx="2" fill={PC_COL.booked} opacity="0.4" />}
                <text x={x0} y={H - 6} textAnchor="middle" fontSize="10.5" fontWeight={i === nowMonth ? 800 : 500}
                  fill={i === nowMonth ? "var(--fg)" : "var(--fg-3)"}>{narrow ? m.label.slice(0, 1) : m.label}</text>
              </g>
            );
          })}
          {breakeven != null && <line x1={L} x2={W - R} y1={y(breakeven)} y2={y(breakeven)} stroke={PC_COL.be} strokeWidth="2" strokeDasharray="6 4" />}
          {required != null && <line x1={L} x2={W - R} y1={y(required)} y2={y(required)} stroke={PC_COL.req} strokeWidth="2" />}
          {hasCap && <path d={capPath} fill="none" stroke={PC_COL.cap} strokeWidth="2" strokeDasharray="1.5 3.5" strokeLinecap="round" />}
          {/* hit targets: the whole column, bigger than any mark */}
          {months.map((_: any, i: number) => (
            <rect key={"h" + i} x={L + cw * i} y={T} width={cw} height={ph + B} fill="transparent" style={{ cursor: "pointer" }}
              onMouseEnter={() => setActive(i)} onClick={() => setActive(active === i ? null : i)} />
          ))}
        </svg>
        {a && (
          <div className="card" role="status" style={{ position: "absolute", top: 4, left: tipLeft, width: TIP, padding: "10px 12px", fontSize: 12, pointerEvents: "none", boxShadow: "var(--shadow)", zIndex: 2 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>{a.label} {year}</div>
            {[["Planned", a.plan, null], ["Completed", a.actual, null], ["Scheduled", a.sched, null],
              ["Break-even", breakeven, PC_COL.be], ["Required for target net", required, PC_COL.req],
              ["Capacity", a.cap, PC_COL.cap]].map(([lb, v, c]: any) => (
              <div key={lb} className="row between nowrap" style={{ gap: 8, lineHeight: 1.55 }}>
                <span className="muted" style={{ minWidth: 0 }}>{c && <span style={{ display: "inline-block", width: 8, height: 2, background: c, verticalAlign: "middle", marginRight: 5 }} />}{lb}</span>
                <span className="tnum" style={{ fontWeight: 700 }}>{v == null ? "—" : PC_money0(v)}</span>
              </div>
            ))}
            {required != null && (
              <div className="row between nowrap" style={{ gap: 8, borderTop: "1px solid var(--line)", marginTop: 5, paddingTop: 5 }}>
                <span className="muted">Plan vs required</span>
                <span className="tnum" style={{ fontWeight: 800, color: a.plan >= required ? "var(--green)" : "var(--amber)" }}>{a.plan >= required ? "+" : "−"}{PC_money0(Math.abs(a.plan - required))}</span>
              </div>
            )}
          </div>
        )}
      </div>
      {/* legend: always present, identity never by color alone (dash patterns + words) */}
      <div className="row gap-12" style={{ flexWrap: "wrap", fontSize: 11.5, marginTop: 8, color: "var(--fg-2)" }}>
        <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--teal-tint)", border: "1px solid var(--teal)" }} />Planned</span>
        <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--teal)" }} />Completed<span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--teal)", opacity: 0.4, marginLeft: 4 }} />Scheduled</span>
        <span className="row" style={{ gap: 6 }}><svg width="18" height="6"><line x1="0" x2="18" y1="3" y2="3" stroke={PC_COL.be} strokeWidth="2" strokeDasharray="5 3" /></svg>Break-even</span>
        <span className="row" style={{ gap: 6 }}><svg width="18" height="6"><line x1="0" x2="18" y1="3" y2="3" stroke={PC_COL.req} strokeWidth="2" /></svg>Required for target net</span>
        {hasCap && <span className="row" style={{ gap: 6 }}><svg width="18" height="6"><line x1="1" x2="17" y1="3" y2="3" stroke={PC_COL.cap} strokeWidth="2" strokeDasharray="1.5 3.5" strokeLinecap="round" /></svg>Crew capacity{capClipped ? ` (up to ${PC_short(capMax)}/mo — above the chart)` : ""}</span>}
      </div>
    </div>
  );
}

export { PlanChart, useWidth };
